if ( typeof ShippingCalculator !== 'function' ) {

	class ShippingCalculator extends HTMLElement {

		#activeRequestController = null;

		#isLoading = false;

		constructor(){

			super();

			this.estimateShipping = this.querySelector('.js-estimate-shipping');
			this.country = this.querySelector('[name="country"]');
			this.province = this.querySelector('[name="province"]');
			this.zip = this.querySelector('[name="zip"]');
			this.resultsBlock = this.querySelector('.shipping-estimator__results');
			this.resultsBlockContent = this.querySelector('.shipping-estimator__results-content');
			this.resultsBlockContentList = this.querySelector('.shipping-estimator__results-content ul');
			this.resultsBlockContentHeading = this.querySelector('.shipping-estimator__results-content-heading');
			this.resultsErrors = this.querySelector('.alert');

			this.#initializeCountryProvinceSelector();
			this.#bindEvents();
		}

		disconnectedCallback() {
			this.#activeRequestController?.abort();
		}

		#initializeCountryProvinceSelector() {
			if (!this.country || !this.province || typeof Shopify?.CountryProvinceSelector !== 'function') return;

			setTimeout(() => {
				new Shopify.CountryProvinceSelector(this.country.id, this.province.id, {
					hideElement: 'address_province_container'
				});

				this.#hideProvinceField();
			});
		}

		#bindEvents() {
			if (!this.estimateShipping || !this.zip) return;

			this.estimateShipping.addEventListener('click', this.#handleEstimateShipping);
			this.zip.addEventListener('input', this.#handleZipInput);
			this.country?.addEventListener('change', () => {
				this.#hideProvinceField();
				this.#resetResults();
			});
		}

		#handleZipInput = (event) => {
			const digits = event.target.value.replace(/\D/g, '').slice(0, 8);
			const formattedZip = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;

			event.target.value = formattedZip;
			this.#resetResults();
		};

		#handleEstimateShipping = async (event) => {
			event.preventDefault();

			if (this.#isLoading) {
				this.#activeRequestController?.abort();
			}

			const zip = this.zip.value.trim();
			const normalizedZip = zip.replace(/\D/g, '');

			if (normalizedZip.length !== 8) {
				this.#showErrors(['CEP inválido']);
				return;
			}

			this.#activeRequestController = new AbortController();
			this.#setLoadingState(true);
			this.#resetResults();

			const countryValue = this.country?.value || 'Brazil';
			const provinceValue = this.province?.value || '';
			const query = new URLSearchParams({
				'shipping_address[zip]': normalizedZip,
				'shipping_address[country]': countryValue,
				'shipping_address[province]': provinceValue
			});

			try {
				const response = await fetch(`${KROWN.settings.routes.cart_url}/shipping_rates.json?${query.toString()}`, {
					credentials: 'same-origin',
					method: 'GET',
					signal: this.#activeRequestController.signal
				});
				const result = await response.json();

				if (result.country || result.zip) {
					this.#showErrors(Object.values(result));
					return;
				}

				this.#showRates(result.shipping_rates || []);
			} catch (error) {
				if (error.name === 'AbortError') return;

				this.#showErrors(['Não foi possível calcular o frete. Tente novamente.']);
			} finally {
				this.#setLoadingState(false);
			}
		};

		#resetResults() {
			if (this.resultsBlock) {
				this.resultsBlock.style.display = 'none';
			}

			if (this.resultsBlockContent) {
				this.resultsBlockContent.style.display = 'none';
			}

			if (this.resultsBlockContentList) {
				this.resultsBlockContentList.innerHTML = '';
			}

			if (this.resultsErrors) {
				this.resultsErrors.style.display = 'none';
				this.resultsErrors.innerHTML = '';
			}
		}

		#showErrors(errors) {
			this.#resetResults();
			if (!this.resultsBlock || !this.resultsErrors) return;

			this.resultsBlock.style.display = 'block';
			this.resultsErrors.style.display = 'block';
			this.resultsErrors.innerHTML = `<ul>${errors.map(error => `<li>${error}</li>`).join('')}</ul>`;
		}

		#showRates(shippingRates) {
			const rates = Array.isArray(shippingRates) ? shippingRates : Object.values(shippingRates);

			this.#resetResults();
			if (!this.resultsBlock || !this.resultsBlockContent || !this.resultsBlockContentList || !this.resultsBlockContentHeading) return;

			this.resultsBlock.style.display = 'block';
			this.resultsBlockContent.style.display = 'block';
			this.resultsBlockContentHeading.textContent = '';

			this.resultsBlockContentList.innerHTML = rates.map(rate => {
				const rawPrice = Number(rate.price);
				const priceInCents = Math.round(rawPrice * 100);
				const isFree = priceInCents === 0;
				const priceMarkup = isFree
					? '<span class="shipping-estimator__rate-price shipping-estimator__rate-price--free">GRÁTIS</span>'
					: `<span class="shipping-estimator__rate-price">${this._formatMoney(priceInCents, KROWN.settings.shop_money_format)}</span>`;

				return `
					<li>
						<span class="shipping-estimator__rate-name">${rate.name}</span>
						${priceMarkup}
					</li>
				`;
			}).join('');
		}

		#setLoadingState(isLoading) {
			this.#isLoading = isLoading;
			if (!this.estimateShipping) return;

			this.estimateShipping.toggleAttribute('disabled', isLoading);
			this.estimateShipping.classList.toggle('processing', isLoading);
		}

		#hideProvinceField() {
			const provinceContainer = this.province?.closest('.cart__shipping-calculator-hidden-field') || this.querySelector('#address_province_container');
			if (!provinceContainer) return;

			provinceContainer.hidden = true;
			provinceContainer.setAttribute('aria-hidden', 'true');
			provinceContainer.style.setProperty('display', 'none', 'important');
		}
		
		_formatMoney(cents, format) {

			if (typeof cents === 'string') {
				cents = cents.replace('.', '');
			}
	
			let value = '';
			const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
			const formatString = format || moneyFormat;
	
			function formatWithDelimiters(number, precision, thousands, decimal) {
				thousands = thousands || ',';
				decimal = decimal || '.';
	
				if (isNaN(number) || number === null) {
					return 0;
				}
	
				number = (number / 100.0).toFixed(precision);
	
				const parts = number.split('.');
				const dollarsAmount = parts[0].replace(
					/(\d)(?=(\d\d\d)+(?!\d))/g,
					'$1' + thousands
				);
				const centsAmount = parts[1] ? decimal + parts[1] : '';
	
				return dollarsAmount + centsAmount;
			}
	
			switch (formatString.match(placeholderRegex)[1]) {
				case 'amount':
					value = formatWithDelimiters(cents, 2);
					break;
				case 'amount_no_decimals':
					value = formatWithDelimiters(cents, 0);
					break;
				case 'amount_with_comma_separator':
					value = formatWithDelimiters(cents, 2, '.', ',');
					break;
				case 'amount_no_decimals_with_comma_separator':
					value = formatWithDelimiters(cents, 0, '.', ',');
					break;
				case 'amount_no_decimals_with_space_separator':
					value = formatWithDelimiters(cents, 0, ' ');
					break;
				case 'amount_with_apostrophe_separator':
					value = formatWithDelimiters(cents, 2, "'");
					break;
			}
	
			return formatString.replace(placeholderRegex, value);
	
		}

	}

  if ( typeof customElements.get('shipping-calculator') == 'undefined' ) {
    customElements.define('shipping-calculator', ShippingCalculator);
	}

}